import React, { useEffect, useRef, useState, useCallback } from "react";
import YouTube from "react-youtube";
import { QRCodeSVG } from "qrcode.react";
import { Maximize2, Minimize2, Search } from "lucide-react";
import { bus } from "../lib/broadcast";
import PresentationOverlay from "../components/karaoke/PresentationOverlay";
import PhoneMockup from "../components/karaoke/PhoneMockup";
import ReactionsOverlay from "../components/karaoke/ReactionsOverlay";
import RatingOverlay from "../components/karaoke/RatingOverlay";
import DedicationBanner from "../components/karaoke/DedicationBanner";
import EndOfVideoOverlay from "../components/karaoke/EndOfVideoOverlay";

// Static player options — defined once at module scope so the reference stays
// stable across renders and react-youtube never reloads the iframe needlessly.
const YT_OPTS = {
  width: "100%",
  height: "100%",
  playerVars: {
    autoplay: 0,
    controls: 0,
    modestbranding: 1,
    rel: 0,
    iv_load_policy: 3,
    fs: 1,
    playsinline: 1,
    disablekb: 1,
  },
};

const SecondScreen = () => {
  const [song, setSong] = useState(null);
  const [presenting, setPresenting] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [qrUrl, setQrUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rating, setRating] = useState(null);
  const [activeDedication, setActiveDedication] = useState(null); // { dedication, singer, key }
  const [showEndOverlay, setShowEndOverlay] = useState(false);
  const [recovery, setRecovery] = useState(null); // { active, singer } discreet "buscando otra versión…" notice
  const playerRef = useRef(null);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      // Cross-window fullscreen may be blocked; the user can press F11
      console.warn("Fullscreen request blocked", e);
    }
  }, []);

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  // Listen for direct postMessage from the main window (carries delegated fullscreen activation)
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "request-fullscreen") {
        toggleFullscreen();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [toggleFullscreen]);

  useEffect(() => {
    bus.post({ type: "request-state" });
    bus.post({ type: "second-alive" });

    // Heartbeat every 2s so the main console knows we're alive
    const heartbeat = setInterval(() => {
      bus.post({ type: "second-alive" });
    }, 2000);

    // Tell main when this window closes
    const announceClose = () => bus.post({ type: "second-closed" });
    window.addEventListener("beforeunload", announceClose);
    window.addEventListener("pagehide", announceClose);

    const unsub = bus.on((msg) => {
      if (!msg) return;
      switch (msg.type) {
        case "state": {
          const { queue, currentIndex, playNowSong, presenting: p, isPlaying: pl, volume: v } = msg;
          setVolume(v ?? 80);
          setPresenting(p || null);
          if (playNowSong) {
            setSong(playNowSong);
            setIsPlaying(pl);
          } else if (currentIndex >= 0 && queue?.[currentIndex]) {
            setSong(queue[currentIndex]);
            setIsPlaying(pl);
          } else {
            setSong(null);
            setIsPlaying(false);
          }
          break;
        }
        case "present":
          setPresenting({ singer: msg.singer, dedication: msg.dedication || null });
          setSong(msg.song);
          setIsPlaying(false);
          break;
        case "play":
          setPresenting(null);
          setShowEndOverlay(false);
          setSong(msg.song);
          setIsPlaying(true);
          if (msg.song?.dedication) {
            setActiveDedication({
              dedication: msg.song.dedication,
              singer: msg.song.singer,
              key: `${msg.song.id || msg.song.videoId}-${Date.now()}`,
            });
          } else {
            setActiveDedication(null);
          }
          break;
        case "pause":
          setIsPlaying(false);
          break;
        case "resume":
          setIsPlaying(true);
          break;
        case "restart":
          setShowEndOverlay(false);
          setIsPlaying(true);
          try {
            if (playerRef.current) {
              playerRef.current.seekTo(0, true);
              playerRef.current.playVideo();
            }
          } catch (e) {
            console.error("Second screen restart failed", e);
          }
          break;
        case "stop":
          setIsPlaying(false);
          setSong(null);
          setPresenting(null);
          setActiveDedication(null);
          break;
        case "show-qr":
          setQrUrl(msg.url);
          break;
        case "hide-qr":
          setQrUrl(null);
          break;
        case "recovery":
          setRecovery(msg.active ? { active: true, singer: msg.singer || null } : null);
          break;
        default:
          break;
      }
    });

    return () => {
      clearInterval(heartbeat);
      window.removeEventListener("beforeunload", announceClose);
      window.removeEventListener("pagehide", announceClose);
      announceClose();
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!playerRef.current) return;
    try {
      if (isPlaying) playerRef.current.playVideo();
      else playerRef.current.pauseVideo();
    } catch (e) {
      console.error("Player play/pause failed", e);
    }
  }, [isPlaying, song?.videoId]);

  useEffect(() => {
    if (!playerRef.current) return;
    try {
      playerRef.current.setVolume(volume);
    } catch (e) {
      console.error("Player setVolume failed", e);
    }
  }, [volume]);

  // Keep playback alive when tab is hidden / window minimized.
  // Browsers can pause/throttle background tabs; we force-resume on visibility.
  useEffect(() => {
    const resume = () => {
      if (!playerRef.current) return;
      try {
        if (isPlaying) playerRef.current.playVideo();
      } catch (e) {
        console.error("Player resume on visibility failed", e);
      }
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
    };
  }, [isPlaying]);

  // Media Session: register as media source so Chrome treats this as music app
  useEffect(() => {
    if (!("mediaSession" in navigator) || !song) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: song.title || "Karaoke",
        artist: song.singer || "Invitado",
        album: "DJ Gabo Karaoke",
        artwork: song.thumbnail ? [{ src: song.thumbnail, sizes: "480x360", type: "image/jpeg" }] : [],
      });
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch (e) {
      console.error("MediaSession metadata update failed", e);
    }
  }, [song, isPlaying]);

  // Silent Web Audio loop keeps Chrome marking the tab as "audible"
  // → prevents the iframe from being throttled / paused in background
  const audioCtxRef = useRef(null);
  useEffect(() => {
    if (!isPlaying) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.0001; // effectively inaudible
        osc.frequency.value = 80;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        audioCtxRef.current = { ctx, osc };
      } else if (audioCtxRef.current.ctx.state === "suspended") {
        audioCtxRef.current.ctx.resume();
      }
    } catch (e) {
      console.error("Silent audio keep-alive failed", e);
    }
  }, [isPlaying]);

  const onReady = (e) => {
    playerRef.current = e.target;
    e.target.setVolume(volume);
    if (isPlaying) e.target.playVideo();
  };

  const onEnd = () => {
    setShowEndOverlay(true);
    bus.post({ type: "ended-from-second" });
  };

  const onError = (e) => {
    const code = e?.data;
    if ((code === 101 || code === 150) && song?.videoId) {
      // Do NOT auto-advance. Let the console recover (invalidate + prompt DJ).
      bus.post({ type: "unplayable", reason: `youtube-error-${code}`, videoId: song.videoId });
    }
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {song ? (
        <div className="absolute inset-0">
          <YouTube
            videoId={song.videoId}
            opts={YT_OPTS}
            onReady={onReady}
            onEnd={onEnd}
            onError={onError}
            className="w-full h-full"
            iframeClassName="w-full h-full"
          />
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white bg-gradient-to-b from-[#0a0a0a] via-black to-[#0a0a0a]">
          <h1 className="font-display text-6xl sm:text-8xl font-black uppercase tracking-tighter">
            <span className="text-primary neon-pink">DJ Gabo</span> Karaoke
          </h1>
          <p className={`mt-4 font-mono tracking-widest uppercase text-sm ${recovery?.active ? "text-secondary" : "text-muted-foreground"}`}>
            {recovery?.active ? "Buscando otra versión..." : "Esperando próxima canción..."}
          </p>
        </div>
      )}
      {presenting && (
        <PresentationOverlay
          singer={(presenting && (presenting.singer || presenting)) || ""}
          dedication={presenting?.dedication}
          onSkip={() => {}}
          mode="fullscreen"
        />
      )}
      {qrUrl && <QrOverlay url={qrUrl} />}
      {/* Compact permanent QR over the video: visible only while a song is active AND the big QR is OFF.
          Reuses the same Guest URL + QRCodeSVG. Never shown together with the big QR (qrUrl gate). */}
      {song && !qrUrl && <CompactQr url={`${window.location.origin}/guest`} />}
      {rating && <RatingOverlay {...rating} />}
      {activeDedication && (
        <DedicationBanner
          key={activeDedication.key}
          dedication={activeDedication.dedication}
          singer={activeDedication.singer}
        />
      )}
      {showEndOverlay && !presenting && !rating && <EndOfVideoOverlay mode="fullscreen" />}
      {recovery?.active && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 z-[46] pointer-events-none flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-black/70 border border-white/15 backdrop-blur-md"
          data-testid="second-screen-recovery-notice"
        >
          <Search className="w-4 h-4 text-primary animate-pulse" />
          <span className="font-mono text-sm uppercase tracking-widest text-white/90">
            {recovery.singer ? `Un momento, ${recovery.singer}…` : "Buscando otra versión…"}
          </span>
        </div>
      )}
      <ReactionsOverlay />

      {/* Fullscreen toggle in corner */}
      <button
        onClick={toggleFullscreen}
        data-testid="second-fullscreen-toggle"
        className="fixed top-4 right-4 z-[60] w-11 h-11 rounded-full bg-black/60 backdrop-blur-md border border-white/20 hover:border-secondary hover:bg-secondary/20 text-white hover:text-secondary flex items-center justify-center transition-all opacity-50 hover:opacity-100"
        title={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
      >
        {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
      </button>
    </div>
  );
};

const CompactQr = ({ url }) => (
  <div
    data-testid="compact-qr-overlay"
    className="fixed z-[45] pointer-events-none"
    style={{ bottom: "clamp(16px, 2.5vw, 30px)", left: "clamp(16px, 2.5vw, 30px)" }}
  >
    <div className="bg-white rounded-xl shadow-[0_6px_28px_rgba(0,0,0,0.55)] p-2.5 flex flex-col items-center gap-1.5">
      <QRCodeSVG
        value={url}
        size={140}
        level="M"
        style={{ width: "clamp(90px, 8vw, 140px)", height: "clamp(90px, 8vw, 140px)" }}
      />
      <span className="text-black font-semibold text-[10px] sm:text-xs text-center leading-tight" style={{ maxWidth: "clamp(90px, 8vw, 140px)" }}>
        Escanea para pedir canción
      </span>
    </div>
  </div>
);

const QrOverlay = ({ url }) => (
  <div
    className="fixed inset-0 z-40 flex items-center justify-center bg-black/95 backdrop-blur-xl overflow-hidden"
    data-testid="second-screen-qr-overlay"
  >
    {/* Ambient grain + radial glow */}
    <div className="absolute inset-0 grain pointer-events-none" />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(circle at 30% 50%, rgba(255,42,109,0.18), transparent 50%), radial-gradient(circle at 75% 50%, rgba(5,217,232,0.14), transparent 55%)",
      }}
    />

    <div className="relative z-10 w-full max-w-[1400px] mx-auto px-10 py-6 flex flex-col gap-5">
      {/* Top heading */}
      <div className="text-center">
        <p className="text-secondary font-mono text-[10px] sm:text-xs tracking-[0.4em] uppercase mb-2 animate-slide-up">
          ▸ Únete a la fiesta
        </p>
        <h1 className="font-display font-black uppercase tracking-tighter text-white text-3xl sm:text-5xl lg:text-6xl leading-[0.95] animate-presentation-in">
          Escanea <span className="text-primary neon-pink">y canta</span>
        </h1>
      </div>

      {/* Body: QR on the left, phone mockup + steps on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-16 items-center justify-items-center">
        {/* Left column: QR + URL */}
        <div className="flex flex-col items-center">
          <div className="p-4 bg-white rounded-2xl shadow-[0_0_60px_rgba(255,42,109,0.45)]">
            <QRCodeSVG value={url} size={210} level="M" />
          </div>
          <p className="mt-3 text-muted-foreground font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-center">
            o entra a
          </p>
          <code className="text-secondary text-xs sm:text-sm font-mono mt-1 text-center break-all max-w-[240px]">
            {url.replace(/^https?:\/\//, "")}
          </code>
        </div>

        {/* Right column: phone mockup + steps */}
        <div className="flex flex-col items-center lg:items-start gap-5 max-w-xl">
          <div className="relative scale-[0.78] origin-top-left lg:origin-top">
            <PhoneMockup />
          </div>

          <div className="space-y-1.5 max-w-md -mt-12 lg:-mt-16">
            <Step n={1} text="Escanea el QR con tu celular" />
            <Step n={2} text="Ingresa tu nombre" />
            <Step n={3} text="Busca tu canción favorita" />
            <Step n={4} text="Toca ENVIAR y espera tu turno" highlight />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const Step = ({ n, text, highlight = false }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
        highlight
          ? "bg-primary text-primary-foreground glow-pink"
          : "bg-white/10 text-white"
      }`}
    >
      {n}
    </div>
    <p
      className={`font-display text-base sm:text-lg ${
        highlight ? "text-primary font-bold" : "text-white/85"
      }`}
    >
      {text}
    </p>
  </div>
);

export default SecondScreen;
